import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { receiptsAPI } from '@matix/api-client';
import type { PrintConfig } from '@matix/types';
import { appendOrderBarcode, buildReceiptHtml, buildReprintData, printReceipt } from '@matix/receipt';
import { toast } from '../../store/toast';
import { currentPrintLanguage } from '../print/printLanguage';
import { PRINT_CONFIG_QUERY_KEY } from '../print/usePrintConfig';

/**
 * Print a LAUNDRY order's take-in ticket: the tenant's ordinary receipt (built-
 * in slip or their own design, exactly as a reprint builds it), with the order
 * barcode appended, twice — the customer's copy and the bag tag (the user's
 * choice, 2026-10-01: thermal printer, two copies, one barcode per order).
 *
 * The barcode carries the invoice number, which is what the laundry screen's
 * scan box and `GET /laundry/orders/lookup` read back.
 */
export function useLaundryPrint(): (receiptId: number) => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    async (receiptId: number): Promise<void> => {
      try {
        const printConfig = queryClient.getQueryData<PrintConfig>(PRINT_CONFIG_QUERY_KEY);
        if (!printConfig) {
          toast('Print config not loaded — please reload the page', 'error');
          return;
        }
        const detail = await receiptsAPI.getById(receiptId);
        const lang = currentPrintLanguage();
        const slip = buildReceiptHtml({ ...buildReprintData(printConfig, detail, lang), language: lang });
        const code = String(detail.entry_no);
        const captions =
          lang === 'ar' ? ['نسخة العميل', 'بطاقة الكيس'] : ['Customer copy', 'Bag tag'];
        for (const caption of captions) {
          await printReceipt(appendOrderBarcode(slip, code, caption));
        }
      } catch (err) {
        toast(err instanceof Error ? err.message : 'Failed to print the laundry ticket', 'error');
      }
    },
    [queryClient],
  );
}
